import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCapabilities } from '../capabilities';
import { navRegistry, settingsRegistry, resolveEntries } from '../registries';
import { moduleNavEntries, moduleSettingsEntries } from '../host';
import { search, searchKeys } from '../api/search.gen';
import type { CommandPaletteItem } from '../../components/ui';

// A palette row that knows where it goes.
export interface CommandResult extends CommandPaletteItem {
  to: string;
}

// Backend entity → palette group label. Records (subjects/inspections/…) join
// in 3-7; today only members come from the index.
const GROUP_LABEL: Record<string, string> = { member: 'Members' };

// Universal-search hook (story 1E-1, D42). Merges two sources, both permission-
// aware: local registry entries (nav + settings, filtered by the capability
// document via resolveEntries) and remote indexed records from the clamped
// /search endpoint. New client sources or backend entities extend this without
// touching the palette component.
export function useCommandSearch(query: string): { items: CommandResult[]; loading: boolean } {
  const caps = useCapabilities();
  const q = query.trim();

  const local = useMemo<CommandResult[]>(() => {
    if (!q) return [];
    const needle = q.toLowerCase();
    const match = (entries: ReturnType<typeof resolveEntries>, group: string, prefix: string): CommandResult[] =>
      entries
        .filter((entry) => entry.label.toLowerCase().includes(needle))
        .flatMap((entry) => (entry.to ? [{ id: `${prefix}:${entry.key}`, title: entry.label, group, to: entry.to }] : []));

    const nav = match(resolveEntries([...navRegistry, ...moduleNavEntries()], caps), 'Navigation', 'nav');
    const settings = match(resolveEntries([...settingsRegistry, ...moduleSettingsEntries()], caps), 'Settings', 'set');
    return [...nav, ...settings];
  }, [q, caps]);

  const { data, isFetching } = useQuery({
    queryKey: searchKeys.list({ q }),
    queryFn: () => search({ q }),
    enabled: q.length >= 1,
    staleTime: 30_000,
  });

  const remote: CommandResult[] = (data?.hits ?? []).map((hit) => ({
    id: `${hit.entity}:${hit.refId}`,
    title: hit.title,
    subtitle: hit.subtitle ?? undefined,
    group: GROUP_LABEL[hit.entity] ?? hit.entity,
    to: hit.url,
  }));

  return { items: [...local, ...remote], loading: q.length >= 1 && isFetching };
}
